/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

// 記事詳細でスマホ幅の場合にテーブルをスクロールできるようにする
// https://github.com/NinNinInc/tabi/pull/2335
// https://github.com/NinNinInc/tabi/issues/2321
// に対応するためにカスタムプラグインで対応
import type {
  HTMLTableElementWithWithTableSelectionState,
  InsertTableCommandPayload,
  TableObserver,
} from "@lexical/table";
import type { NodeKey } from "lexical";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $computeTableMap,
  $computeTableMapSkipCellCheck,
  $createTableCellNode,
  $createTableNodeWithDimensions,
  $getNodeTriplet,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  applyTableHandlers,
  INSERT_TABLE_COMMAND,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import {
  $insertFirst,
  $insertNodeToNearestRoot,
  mergeRegister,
  $wrapNodeInElement,
} from "@lexical/utils";
import {
  $createParagraphNode,
  $getNodeByKey,
  $isTextNode,
  $nodesOfType,
  COMMAND_PRIORITY_EDITOR,
} from "lexical";
import { useEffect, useRef } from "react";
import invariant from "../shared/invariant";
import { CustomTableNode } from "../nodes/CustomTableNode";
import {
  $createTableBlockNode,
  CustomTableBlockNode,
} from "../nodes/CustomTableBlockNode";
import { $createTableInnerNode } from "../nodes/CustomTableInnerNode";
import theme from "../themes/Theme";

export function CustomTablePlugin({
  hasCellMerge = true,
  hasCellBackgroundColor = true,
  hasTabHandler = true,
}: {
  hasCellMerge?: boolean;
  hasCellBackgroundColor?: boolean;
  hasTabHandler?: boolean;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  const scrollableTables = useRef([]);

  useEffect(() => {
    if (!editor.hasNodes([TableNode, TableCellNode, TableRowNode])) {
      invariant(
        false,
        "TablePlugin: TableNode, TableCellNode or TableRowNode not registered on editor"
      );
    }

    return mergeRegister(
      editor.registerCommand<InsertTableCommandPayload>(
        INSERT_TABLE_COMMAND,
        ({ columns, rows, includeHeaders }) => {
          const tableNode = $createTableNodeWithDimensions(
            Number(rows),
            Number(columns),
            includeHeaders
          );
          $insertNodeToNearestRoot(tableNode);

          const firstDescendant = tableNode.getFirstDescendant();
          if ($isTextNode(firstDescendant)) {
            firstDescendant.select();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerNodeTransform(TableNode, (node) => {
        const [gridMap] = $computeTableMapSkipCellCheck(node, null, null);
        const maxRowLength = gridMap.reduce((curLength, row) => {
          return Math.max(curLength, row.length);
        }, 0);
        for (let i = 0; i < gridMap.length; ++i) {
          const rowLength = gridMap[i].length;
          if (rowLength === maxRowLength) {
            continue;
          }
          const lastCellMap = gridMap[i][rowLength - 1];
          const lastRowCell = lastCellMap.cell;
          for (let j = rowLength; j < maxRowLength; ++j) {
            // TODO: inherit header state from another header or body
            const newCell = $createTableCellNode(0);
            newCell.append($createParagraphNode());
            if (lastRowCell !== null) {
              lastRowCell.insertAfter(newCell);
            } else {
              $insertFirst(lastRowCell, newCell);
            }
          }
        }
      })
    );
  }, [editor]);

  useEffect(() => {
    const tableSelections = new Map<NodeKey, TableObserver>();

    const initializeTableNode = (tableNode: TableNode) => {
      const nodeKey = tableNode.getKey();
      const tableElement = editor.getElementByKey(
        nodeKey
      ) as HTMLTableElementWithWithTableSelectionState;
      if (tableElement && !tableSelections.has(nodeKey)) {
        const tableSelection = applyTableHandlers(
          tableNode,
          tableElement,
          editor,
          hasTabHandler
        );
        tableSelections.set(nodeKey, tableSelection);
      }
    };

    // Plugins might be loaded _after_ initial content is set, hence existing table nodes
    // won't be initialized from mutation[create] listener. Instead doing it here,
    editor.getEditorState().read(() => {
      const tableNodes = $nodesOfType(TableNode);
      tableNodes.concat($nodesOfType(CustomTableNode));
      for (const tableNode of tableNodes) {
        if ($isTableNode(tableNode)) {
          initializeTableNode(tableNode);
        }
      }
    });

    const unregisterMutationListener = editor.registerMutationListener(
      CustomTableNode,
      (nodeMutations) => {
        for (const [nodeKey, mutation] of nodeMutations) {
          // Nodeがoverrideでreplaceされるとupdatedが発火するので再登録する
          if (mutation === "created" || mutation === "updated") {
            editor.getEditorState().read(() => {
              const tableNode = $getNodeByKey<TableNode>(nodeKey);
              if ($isTableNode(tableNode)) {
                initializeTableNode(tableNode);
              }
            });
          } else if (mutation === "destroyed") {
            const tableSelection = tableSelections.get(nodeKey);

            if (tableSelection !== undefined) {
              tableSelection.removeListeners();
              tableSelections.delete(nodeKey);
            }
          }
        }
      }
    );

    return () => {
      unregisterMutationListener();
      // Hook might be called multiple times so cleaning up tables listeners as well,
      // as it'll be reinitialized during recurring call
      for (const [, tableSelection] of tableSelections) {
        tableSelection.removeListeners();
      }
    };
  }, [editor, hasTabHandler]);

  // Unmerge cells when the feature isn't enabled
  useEffect(() => {
    if (hasCellMerge) {
      return;
    }
    return editor.registerNodeTransform(TableCellNode, (node) => {
      if (node.getColSpan() > 1 || node.getRowSpan() > 1) {
        // When we have rowSpan we have to map the entire Table to understand where the new Cells
        // fit best; let's analyze all Cells at once to save us from further transform iterations
        const [, , gridNode] = $getNodeTriplet(node);
        const [gridMap] = $computeTableMap(gridNode, node, node);
        // TODO this function expects Tables to be normalized. Look into this once it exists
        const rowsCount = gridMap.length;
        const columnsCount = gridMap[0].length;
        let row = gridNode.getFirstChild();
        invariant(
          $isTableRowNode(row),
          "Expected TableNode first child to be a RowNode"
        );
        const unmerged = [];
        for (let i = 0; i < rowsCount; i++) {
          if (i !== 0) {
            row = row.getNextSibling();
            invariant(
              $isTableRowNode(row),
              "Expected TableNode first child to be a RowNode"
            );
          }
          let lastRowCell: null | TableCellNode = null;
          for (let j = 0; j < columnsCount; j++) {
            const cellMap = gridMap[i][j];
            const cell = cellMap.cell;
            if (cellMap.startRow === i && cellMap.startColumn === j) {
              lastRowCell = cell;
              unmerged.push(cell);
            } else if (cell.getColSpan() > 1 || cell.getRowSpan() > 1) {
              invariant(
                $isTableCellNode(cell),
                "Expected TableNode cell to be a TableCellNode"
              );
              const newCell = $createTableCellNode(cell.__headerState);
              if (lastRowCell !== null) {
                lastRowCell.insertAfter(newCell);
              } else {
                $insertFirst(row, newCell);
              }
            }
          }
        }
        for (const cell of unmerged) {
          cell.setColSpan(1);
          cell.setRowSpan(1);
        }
      }
    });
  }, [editor, hasCellMerge]);

  // Remove cell background color when feature is disabled
  useEffect(() => {
    if (hasCellBackgroundColor) {
      return;
    }
    return editor.registerNodeTransform(TableCellNode, (node) => {
      if (node.getBackgroundColor() !== null) {
        node.setBackgroundColor(null);
      }
    });
  }, [editor, hasCellBackgroundColor, hasCellMerge]);

  // Read Modeの時だけラッパー用のNodeを追加して
  // 横スクロール用の処理をする
  useEffect(() => {
    if (!editor.isEditable()) {
      editor.update(() => {
        const tableNodes = $nodesOfType(CustomTableNode);
        for (const tableNode of tableNodes) {
          if ($isTableNode(tableNode)) {
            const firstRow = tableNode.getFirstChildOrThrow<TableRowNode>();

            if (!$isTableRowNode(firstRow)) {
              throw new Error("Expected to find row node.");
            }

            const colCount = firstRow.getChildrenSize();
            const isScrollable = colCount >= 4;

            tableNode.setIsScrollable(isScrollable);
            const tableInnerNode = $wrapNodeInElement(
              tableNode,
              $createTableInnerNode
            );
            const createTableBlockNode = () => {
              return $createTableBlockNode(isScrollable);
            };
            $wrapNodeInElement(tableInnerNode, createTableBlockNode);
          }
        }
      });

      editor.registerUpdateListener(({ editorState }) => {
        if (scrollableTables.current && scrollableTables.current.length > 0) {
          return;
        }
        editorState.read(() => {
          const tableBlockNodes = $nodesOfType(CustomTableBlockNode);
          if (!tableBlockNodes || tableBlockNodes.length === 0) {
            return;
          }
          for (const tableBlockNode of tableBlockNodes) {
            if (tableBlockNode.getIsScrollable()) {
              const element = editor.getElementByKey(tableBlockNode.getKey());
              if (element) {
                scrollableTables.current.push(element);
              }
            }
          }
          setTableScrollStyles();
          window.addEventListener("resize", onResizeWindow);
        });
      });
    }
    return () => {
      if (!editor.isEditable()) {
        unsetTableScrollStyles();
        window.removeEventListener("resize", onResizeWindow);
      }
    };
  }, []);

  const onResizeWindow = () => {
    [...scrollableTables.current].map((table) => {
      const innerElement = table.querySelector(`.${theme.tableBlockInner}`);
      if (innerElement) {
        updateTableScrollStyle(innerElement);
      }
    });
  };

  const setTableScrollStyles = () => {
    [...scrollableTables.current].map((table) => {
      const innerElement = table.querySelector(`.${theme.tableBlockInner}`);
      if (innerElement) {
        innerElement.addEventListener("scroll", handleScrollEvent);
        updateTableScrollStyle(innerElement);
      }
    });
  };

  const unsetTableScrollStyles = () => {
    [...scrollableTables.current].map((table) => {
      const innerElement = table.querySelector(`.${theme.tableBlockInner}`);
      if (innerElement) {
        innerElement.removeEventListener("scroll", handleScrollEvent);
      }
    });
  };

  const handleScrollEvent = (e: Event) => {
    const target = e.target as HTMLElement;
    updateTableScrollStyle(target);
  };

  const updateTableScrollStyle = (target: HTMLElement) => {
    const startClass = "scroll--start";
    const endClass = "scroll--end";
    const parent = target.parentElement;
    if (parent.classList.contains(`.${theme.tableBlockScrollable}`)) {
      return;
    }
    const scrollPos = target.scrollLeft;
    const totalScrolledWidth = target.scrollLeft + target.clientWidth;
    if (scrollPos <= 0) {
      parent.classList.add(endClass);
      parent.classList.remove(startClass);
    } else if (scrollPos > 0) {
      parent.classList.add(startClass);
      if (totalScrolledWidth >= target.scrollWidth - 1) {
        parent.classList.remove(endClass);
      } else {
        parent.classList.add(endClass);
      }
    }
  };

  return null;
}
